import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, addMonths, endOfMonth, format, parseISO, startOfMonth, isSameMonth } from 'date-fns';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Stack from 'react-bootstrap/Stack';
import { useForm } from 'react-hook-form';
import { Id as ToastId, toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { StatusCodes } from '../../../../v2/js/constants/workflows';
import {
  type BookingRequestRead,
  BookingRequestsService,
  BookingsService,
  ConnectionsService,
  ProviderLimitedDetail,
  SchedulesService,
  UserTypeEnum,
} from '../../../api';
import { DATE_FORMAT, NOT_SELECTED } from '../../constants/time';
import { SessionContext } from '../../contexts/SessionContext';
import { useCurrentUser } from '../../hooks/auth';
import { bookingRequestsQueryKeys } from '../../query-keys-factories/booking-requests';
import { connectionRequestsQueryKeys } from '../../query-keys-factories/connection-requests';
import { scheduleQueryKeys } from '../../query-keys-factories/schedule';
import { userQueryKeys } from '../../query-keys-factories/user';
import { BookingRequestDialog } from '../BookingRequestDialog';
import { useConfirmationModal } from '../ConfirmationModal';
import { Calendar } from './Calendar/Calendar';
import { CloseConfirmationPopup } from './CloseConfirmationPopup';
import { FacilitySelector } from './FacilitySelector';
import { HCOShiftSelectorModal2 } from './HCOShiftSelectorModal';
import { getDatesUntilYesterday } from './Helper/ShiftSelectorHelper';
import { ProviderShiftSelectorModal2 } from './ProviderShiftSelectorModal/ProviderShiftSelectorModal2';
import { ProviderScheduleWithKey } from './Scheduler.types';
import { Shift } from './ShiftPreset';

import './Scheduler.scss';

interface Props {
  providerId: string;
  provider?: ProviderLimitedDetail;
  isBooking?: boolean;
  readOnly?: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const Scheduler = ({ providerId, provider, readOnly, isBooking = false }: Props) => {
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [connectedFacility, setConnectedFacility] = useState<any>(null);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [isConfirmModalShown, setIsConfirmModalShown] = useState<boolean>(false);
  const [showShiftSelectorModal, setShowShiftSelectorModal] = useState(false);
  const [isEditProviderSchedules, setIsEditProviderSchedules] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<string>('');
  const [bookingRequest, setBookingRequest] = useState<BookingRequestRead | null>(null);
  const { user: currentUser } = useCurrentUser();
  const [visibleMonths, setVisibleMonths] = useState<[Date, Date]>(() => {
    const today = new Date();
    const firstMonth = startOfMonth(today);
    const secondMonth = startOfMonth(addMonths(firstMonth, 1));
    return [firstMonth, secondMonth];
  });
  const calendarDateMin = useRef<Date>(visibleMonths[0]);
  const allSchedulesRef = useRef<{ [key: string]: ProviderScheduleWithKey }>({});
  const { ConfirmationModal, showConfirmationModal } = useConfirmationModal();
  const [showFullDecline, setShowFullDecline] = useState(false);
  const { control } = useForm();
  const { user } = useContext(SessionContext);

  const params: Parameters<typeof SchedulesService.schedulesList>[0] = {
    provider: [providerId],
    dateMin: format(startOfMonth(visibleMonths[0]), DATE_FORMAT),
    dateMax: format(endOfMonth(visibleMonths[1]), DATE_FORMAT),
  };

  const needsFetch = useCallback((month: Date) => {
    return !Object.keys(allSchedulesRef.current).some((dateStr) => {
      const scheduleDate = parseISO(dateStr);
      return isSameMonth(scheduleDate, month);
    });
  }, []);

  const handleChangeMonth = (newDate: Date) => {
    const isMovingForward = format(newDate, 'yyyyMM') > format(visibleMonths[0], 'yyyyMM');

    let newFirstMonth: Date;
    let newSecondMonth: Date;

    if (isMovingForward) {
      // Moving forward one month
      newFirstMonth = startOfMonth(addMonths(visibleMonths[0], 1));
      newSecondMonth = startOfMonth(addMonths(visibleMonths[1], 1));
    } else {
      // Moving backward one month
      newFirstMonth = startOfMonth(addMonths(visibleMonths[0], -1));
      newSecondMonth = startOfMonth(addMonths(visibleMonths[1], -1));
    }

    setVisibleMonths([newFirstMonth, newSecondMonth]);
    calendarDateMin.current = newFirstMonth;

    if (needsFetch(newFirstMonth) || needsFetch(newSecondMonth)) {
      queryClient.invalidateQueries({
        queryKey: scheduleQueryKeys.list({
          provider: [providerId],
          dateMin: format(startOfMonth(newFirstMonth), DATE_FORMAT),
          dateMax: format(endOfMonth(newSecondMonth), DATE_FORMAT),
        }),
      });
    }
  };

  const queryClient = useQueryClient();

  const { isLoading: bookingRequestLoading, refetch: refetchBookingRequest } = useQuery({
    queryKey: bookingRequestsQueryKeys.extra(requestId),
    queryFn: async () => await BookingRequestsService.bookingRequestsRetrieve({ requestId }),
    enabled: !!requestId,
    onSuccess: (data: BookingRequestRead) => setBookingRequest(data),
  });

  const onUpdateStatus = () => {
    queryClient.invalidateQueries({ queryKey: scheduleQueryKeys.list(params) });
  };

  const querySchedules = useQuery({
    queryKey: scheduleQueryKeys.list(params),
    queryFn: async () => {
      return await SchedulesService.schedulesList(params);
    },
    select: (data) => data.map((item) => ({ ...item, key: item?.id || uuidv4() })),
    onSuccess: (data) => {
      data.forEach((schedule) => {
        allSchedulesRef.current[schedule.start_on] = schedule;
      });
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  const connectionQueryParams: Parameters<
    typeof ConnectionsService.connectionsConnectedFacilityList
  >[0] = {
    provider: providerId,
    status: [StatusCodes.ACTIVE, StatusCodes.AWAIT_PRVLG],
    ordering: 'facility__name',
  };
  const queryFacilities = useQuery({
    queryKey: connectionRequestsQueryKeys.list(connectionQueryParams),
    queryFn: async () => {
      return ConnectionsService.connectionsConnectedFacilityList(connectionQueryParams);
    },
    enabled: isBooking && providerId && user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP,
    select: (data) => data.results,
  });

  const mutationScheduleCreate = useMutation({
    mutationFn: async (data: Shift[]) => {
      const schedules = data.map((shift) => {
        const start_on = format(parseISO(shift.startOn), DATE_FORMAT);

        return {
          shift_type: shift.presetType,
          start_on,
        };
      });
      await SchedulesService.schedulesCreate({
        requestBody: { provider_id: providerId, schedules },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleQueryKeys.all() });
    },
  });

  const mutationScheduleDelete = useMutation({
    mutationFn: async () => {
      const dates = selectedDates.map((date) => format(date, DATE_FORMAT));
      await SchedulesService.schedulesDestroy({ provider: providerId, dates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleQueryKeys.all() });
    },
  });

  const mutationBookingCreate = useMutation({
    mutationFn: async (data: { shifts: Shift[]; comment?: string }) => {
      const healthcare_facility_id = facilityId as string;
      const schedules = data.shifts.map((shift) => {
        const date = parseISO(shift.startOn);
        const nextDay = add(date, { days: 1 });

        const end_date =
          shift.startTime >= shift.endTime
            ? format(nextDay, DATE_FORMAT)
            : format(date, DATE_FORMAT);

        return {
          provider_rate_type: shift.presetType,
          start_on: shift.startOn,
          end_on: end_date,
          start_time: shift.startTime,
          end_time: shift.endTime,
        };
      });
      await BookingsService.bookingsCreate({
        requestBody: {
          provider_id: providerId,
          schedules,
          healthcare_facility_id,
          comment: data.comment || '',
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleQueryKeys.all() });
      queryClient.invalidateQueries(userQueryKeys.notifications());
    },
  });

  const handleChangeSelectedDates = (newSelectedDates: Date[]) => {
    const isForEdit = newSelectedDates.some((el) =>
      Object.keys(allSchedulesRef.current).includes(format(el, DATE_FORMAT))
    );
    setIsEditProviderSchedules(isForEdit);
    setSelectedDates(newSelectedDates);
  };

  const handleClickRemoveAvailability = async () => {
    const toastId = toast.loading('Removing availability...');
    try {
      await mutationScheduleDelete.mutateAsync();
      setSelectedDates([]);
      updateToastToSuccess(toastId, 'Availability removed.');
      if (showShiftSelectorModal) {
        setShowShiftSelectorModal(false);
      }
    } catch {
      updateToastToError(toastId);
    }
  };

  const handleClickAddAvailability = useCallback(() => {
    setShowShiftSelectorModal(true);
  }, []);

  const isProvider = useMemo(() => currentUser?.user_type === UserTypeEnum.PROVIDER, [currentUser]);

  const handleClickSendPlacementRequest = () => {
    if (!facilityId || facilityId === NOT_SELECTED) {
      toast.error('Facility is required.');
      return;
    }
    setShowShiftSelectorModal(true);
  };

  const handleCloseShiftSelectorModal = () => {
    setShowShiftSelectorModal(false);
  };

  const createSchedule = async (data: { shifts: Shift[] }) => {
    const toastId = toast.loading('Adding availability...');
    try {
      await mutationScheduleCreate.mutateAsync(data.shifts);
      setShowShiftSelectorModal(false);
      setSelectedDates([]);
      updateToastToSuccess(toastId, 'Availability added.');
    } catch (error) {
      const body: string = error.body?.error || '';
      if (body !== '') {
        updateToastToErrorWithMessage(toastId, body + '.');
      } else {
        updateToastToError(toastId);
      }
    }
  };

  const createBooking = async (data: { shifts: Shift[]; comment?: string }) => {
    const toastId = toast.loading('Sending assignment request...');
    try {
      await mutationBookingCreate.mutateAsync(data);
      setShowShiftSelectorModal(false);
      setSelectedDates([]);
      updateToastToSuccess(toastId, 'Assignment request sent.');
    } catch (error) {
      const body: string = error.body?.error || '';
      if (body.includes("exceeds provider's limit")) {
        updateToastToErrorWithMessage(toastId, body);
      } else {
        updateToastToError(toastId);
      }
    }
  };

  const updateToastToSuccess = (toastId: ToastId, message: string) => {
    toast.update(toastId, {
      render: message,
      type: 'success',
      autoClose: 3000,
      isLoading: false,
    });
  };

  const updateToastToError = (toastId: ToastId) => {
    toast.update(toastId, {
      render: 'Something went wrong. Please try again later.',
      type: 'error',
      autoClose: 3000,
      isLoading: false,
    });
  };

  const updateToastToErrorWithMessage = (toastId: ToastId, message: string) => {
    toast.update(toastId, {
      render: message,
      type: 'error',
      autoClose: 3000,
      isLoading: false,
    });
  };

  const isLoading = mutationScheduleCreate.isLoading || mutationBookingCreate.isLoading;

  const disableButtons = useMemo(() => {
    const baseConditions =
      !selectedDates.length ||
      querySchedules.isLoading ||
      mutationScheduleDelete.isLoading ||
      mutationScheduleCreate.isLoading ||
      mutationBookingCreate.isLoading;

    if (isBooking) {
      return baseConditions || !facilityId || facilityId === NOT_SELECTED;
    }

    return baseConditions;
  }, [
    selectedDates.length,
    querySchedules.isLoading,
    mutationScheduleDelete.isLoading,
    mutationScheduleCreate.isLoading,
    mutationBookingCreate.isLoading,
    isBooking,
    facilityId,
  ]);

  const bookedSchedules = useMemo(() => {
    return querySchedules.data?.filter((schedule) => !schedule?.is_available);
  }, [querySchedules.data]);

  const handleCloseModal = () => {
    setIsConfirmModalShown(true);
  };
  const onCloseConfirmPopup = useCallback(
    () => setIsConfirmModalShown(false),
    [setIsConfirmModalShown]
  );
  const onOpenConfirmPopup = useCallback(
    () => setIsConfirmModalShown(true),
    [setIsConfirmModalShown]
  );
  const disableDates = getDatesUntilYesterday();

  const onHideHandler = () => {
    setBookingRequest(null);
    setRequestId('');
  };

  const hasApiError = useMemo(() => {
    const apiStates = [
      {
        isError: querySchedules.isError,
        isLoading: querySchedules.isLoading,
        isEnabled: true,
      },
      {
        isError: queryFacilities.isError,
        isLoading: queryFacilities.isLoading,
        isEnabled:
          isBooking && !!providerId && user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP,
      },
      {
        isError: mutationScheduleCreate.isError,
        isLoading: mutationScheduleCreate.isLoading,
        isEnabled: true,
      },
      {
        isError: mutationScheduleDelete.isError,
        isLoading: mutationScheduleDelete.isLoading,
        isEnabled: true,
      },
      {
        isError: mutationBookingCreate.isError,
        isLoading: mutationBookingCreate.isLoading,
        isEnabled: true,
      },
    ];

    // Only consider errors for enabled queries/mutations
    return apiStates.some((state) => state.isEnabled && state.isError && !state.isLoading);
  }, [
    querySchedules.isError,
    querySchedules.isLoading,
    queryFacilities.isError,
    queryFacilities.isLoading,
    mutationScheduleCreate.isError,
    mutationScheduleCreate.isLoading,
    mutationScheduleDelete.isError,
    mutationScheduleDelete.isLoading,
    mutationBookingCreate.isError,
    mutationBookingCreate.isLoading,
    isBooking,
    providerId,
    user?.user_type,
  ]);

  const buttonTooltip = useMemo(() => {
    if (!isBooking) return '';

    if (!facilityId) {
      return 'Choose a Facility in the Dropdown above the Calendar';
    }

    if (!selectedDates.length) {
      return 'Select at least one date';
    }

    return '';
  }, [isBooking, facilityId, selectedDates.length]);

  return (
    <Container className="p-0">
      <Modal
        centered
        scrollable
        size="lg"
        backdrop="static"
        className="shift-request-modal"
        keyboard={false}
        show={showShiftSelectorModal}
        onHide={handleCloseModal}
      >
        {isProvider ? (
          <ProviderShiftSelectorModal2
            isBooking={isBooking}
            isEdit={isEditProviderSchedules}
            isLoading={isLoading}
            selectedDates={selectedDates}
            bookedSchedules={bookedSchedules}
            onConfirm={createSchedule}
            onOpenConfirmPopup={onOpenConfirmPopup}
            onRemoveSchedules={handleClickRemoveAvailability}
          />
        ) : (
          <HCOShiftSelectorModal2
            isBooking={isBooking}
            facilityId={facilityId}
            providerId={providerId}
            provider={provider}
            connectedFacility={connectedFacility}
            isLoading={isLoading}
            selectedDates={selectedDates}
            schedules={Object.values(allSchedulesRef.current)}
            setSelectedDates={setSelectedDates}
            onConfirm={createBooking}
            onOpenConfirmPopup={onOpenConfirmPopup}
          />
        )}
      </Modal>
      <CloseConfirmationPopup
        title={'Unsaved Changes'}
        isShown={isConfirmModalShown}
        onClose={onCloseConfirmPopup}
        onConfirm={handleCloseShiftSelectorModal}
        body={
          <>
            <p className="mb-2">You have unsaved changes, are you sure you want to cancel?</p>
            <p className="mb-0">Your current progress will not be saved.</p>
          </>
        }
      />

      <Row className="row-cols-auto justify-content-center">
        <Col>
          <Alert variant="danger" show={hasApiError}>
            <Alert.Heading>Something went wrong</Alert.Heading>
            <p>Please try again later. If the problem persists, please contact us.</p>
          </Alert>
        </Col>
      </Row>

      <Row className="row-cols-auto justify-content-center">
        <Col>
          {isBooking && (
            <Row className="mb-3">
              <Col xs={12} className="facility-selector-container">
                <FacilitySelector
                  connectedFacilities={queryFacilities?.data ?? []}
                  onChange={(selection: { value: string; label: string } | null) => {
                    setFacilityId(selection?.value ?? null);

                    if (selection?.value && queryFacilities?.data) {
                      const selectedFacility = queryFacilities.data.find(
                        (connectedFacility) => connectedFacility.facility.id === selection.value
                      );
                      setConnectedFacility(selectedFacility);
                    } else {
                      setConnectedFacility(null);
                    }

                    if (showShiftSelectorModal) {
                      setShowShiftSelectorModal(false);
                    }
                  }}
                  control={control}
                  name="facility_id"
                />
              </Col>
            </Row>
          )}
          <Calendar
            isProvider={isProvider}
            disabled={disableDates}
            isBooking={isBooking}
            isLoading={querySchedules.isLoading}
            readOnly={readOnly}
            schedules={querySchedules.data ?? []}
            selectedDates={selectedDates}
            onChangeSelectedDates={handleChangeSelectedDates}
            onChangeMonth={handleChangeMonth}
            onClickPending={setRequestId}
            currentMonth={visibleMonths[0]}
          />

          {!readOnly && (
            <>
              {/* Selected dates count - only visible on mobile */}
              <div className="d-md-none d-flex justify-content-between align-items-center px-4 py-2">
                <span className="text-muted" data-testid="selected-dates-count">
                  {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''} selected
                </span>
                {selectedDates.length > 0 && (
                  <Button variant="outline-danger" size="sm" onClick={() => setSelectedDates([])}>
                    Clear
                  </Button>
                )}
              </div>

              {/* Desktop Layout */}
              <div className="d-none d-md-block">
                <Stack
                  className="mt-2 flex-wrap px-4 text-start justify-content-end gap-2"
                  direction="horizontal"
                >
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        const today = startOfMonth(new Date());
                        const nextMonth = startOfMonth(addMonths(today, 1));
                        setVisibleMonths([today, nextMonth]);
                        calendarDateMin.current = today;
                      }}
                    >
                      Today
                    </Button>
                    {selectedDates.length > 0 && (
                      <Button variant="outline-danger" onClick={() => setSelectedDates([])}>
                        Clear All Selected
                      </Button>
                    )}
                  </div>

                  <span className="ms-auto" data-testid="selected-dates-count">
                    {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''} selected
                  </span>

                  <div
                    className="d-flex flex-wrap gap-2 justify-content-end"
                    style={{ minWidth: '280px' }}
                  >
                    {isBooking ? (
                      buttonTooltip ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="facility-selection-tooltip">{buttonTooltip}</Tooltip>
                          }
                        >
                          <span>
                            <Button
                              variant="primary"
                              disabled={disableButtons}
                              onClick={handleClickSendPlacementRequest}
                            >
                              Review Assignment Request
                            </Button>
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <Button
                          variant="primary"
                          disabled={disableButtons}
                          onClick={handleClickSendPlacementRequest}
                        >
                          Review Assignment Request
                        </Button>
                      )
                    ) : (
                      <>
                        <Button
                          variant="danger"
                          disabled={disableButtons}
                          onClick={handleClickRemoveAvailability}
                        >
                          Remove Availability
                        </Button>
                        <Button
                          variant="primary"
                          disabled={disableButtons}
                          onClick={handleClickAddAvailability}
                        >
                          {isEditProviderSchedules ? 'Modify Availability' : 'Add Availability'}
                        </Button>
                      </>
                    )}
                  </div>
                </Stack>
              </div>

              {/* Mobile Layout */}
              <div className="d-md-none d-flex flex-column gap-2 px-4 py-2">
                {/* Action Buttons */}
                <div className="d-flex flex-column gap-2">
                  {isBooking ? (
                    buttonTooltip ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="facility-selection-tooltip">{buttonTooltip}</Tooltip>}
                      >
                        <span className="w-100">
                          <Button
                            variant="primary"
                            className="w-100"
                            disabled={disableButtons}
                            onClick={handleClickSendPlacementRequest}
                          >
                            Review Assignment Request
                          </Button>
                        </span>
                      </OverlayTrigger>
                    ) : (
                      <Button
                        variant="primary"
                        className="w-100"
                        disabled={disableButtons}
                        onClick={handleClickSendPlacementRequest}
                      >
                        Review Assignment Request
                      </Button>
                    )
                  ) : (
                    <>
                      <Button
                        variant="primary"
                        className="w-100"
                        disabled={disableButtons}
                        onClick={handleClickAddAvailability}
                      >
                        {isEditProviderSchedules ? 'Modify Availability' : 'Add Availability'}
                      </Button>
                      <Button
                        variant="danger"
                        className="w-100"
                        disabled={disableButtons}
                        onClick={handleClickRemoveAvailability}
                      >
                        Remove Availability
                      </Button>
                    </>
                  )}
                </div>

                {/* Today Button */}
                <Button
                  variant="outline-primary"
                  className="w-100"
                  onClick={() => {
                    const today = startOfMonth(new Date());
                    const nextMonth = startOfMonth(addMonths(today, 1));
                    setVisibleMonths([today, nextMonth]);
                    calendarDateMin.current = today;
                  }}
                >
                  Today
                </Button>
              </div>
            </>
          )}
        </Col>
      </Row>
      {!!bookingRequest && (
        <BookingRequestDialog
          bookingRequest={bookingRequest}
          show={!!bookingRequest}
          showConfirmationModal={showConfirmationModal}
          onHide={() => onHideHandler()}
          refetch={refetchBookingRequest}
          isLoading={bookingRequestLoading}
          onUpdateStatus={onUpdateStatus}
          setShowFullDecline={setShowFullDecline}
          showFullDecline={showFullDecline}
        />
      )}
      <ConfirmationModal
        key={showFullDecline ? 'decline' : 'accept'}
        title="Assignment Agreement"
        htmlText={
          showFullDecline ? (
            <>
              <h5 className="text-section-name--dark-brand mb-2">
                {bookingRequest?.healthcare_facility.name}
              </h5>
              <div className="">
                Are you sure you want to decline <span className="fw-bold text-danger">ALL</span>{' '}
                shifts?
              </div>
              <div className="text-medium text-muted bg-neutral-200 p-2 rounded mt-3">
                If you continue the assignment will be CANCELED. <br /> <br />
                To accept any shift, go back and place check mark next to it and then click the
                Respond to Assignment button.
              </div>
            </>
          ) : (
            <>
              <h5 className="text-section-name--dark-brand mb-2">
                {bookingRequest?.healthcare_facility.name}
              </h5>
              <div className="">
                By accepting, you <b>acknowledge and agree to</b>
                <ul>
                  <li>dates and times for any shift selected with a checkmark</li>
                  <li>fixed hourly rates noted next to a selected shift</li>
                  <li>
                    Any <b>unchecked</b> shifts will be{' '}
                    <span className="text-danger">rejected</span>
                  </li>
                </ul>
              </div>
              <div className="text-medium text-muted bg-neutral-200 p-2 rounded">
                You also agree to any facility requirements including but not limited to APP
                supervision, chart completion and compliance regulations as outlined in the Provider
                Agreement.
              </div>
            </>
          )
        }
        confirmText={showFullDecline ? 'Decline' : 'Accept'}
        confirmVariant={showFullDecline ? 'danger' : 'primary'}
        cancelText="Go Back"
        cancelVariant="outline-primary"
      />
    </Container>
  );
};
